import React, { useContext, useEffect, useState } from "react";
import { Container, Button, Row, Col } from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { TRANSFER_PATHS } from "../../../../Routes/paths";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import { useTransferService } from "../api/useTransferService";
import SectionCard from "Components/Common/SectionCard";
import FullPageLoader from "Components/Common/FullPageLoader";
import FilePreviewModal from "Components/Common/FilePreviewModal";
import addVolunteerStyle from "../../../../modules/Volunteer/MasterVolunteer/css/volunteer.module.scss";
import DetailField from "../../../../Components/Common/DetailField";
import { BreadcrumbContext } from "Layouts/Header";
import TruncatedText from "Components/Common/TruncatedText";

const ViewTransfer: React.FC = () => {
  const navigate = useNavigate();
  const breadcrumbContext = useContext(BreadcrumbContext);
  const { id } = useParams();

  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewFileUrl, setPreviewFileUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [fromlabel, setfromLabel] = useState("")
  const [tolabel, settoLabel] = useState("")
  const [studycodelabel, setStudycodelabel] = useState(false)

  const handlePreview = (file: any) => {
      const API_BASE_URL =  sessionStorage.getItem("baseUrl") || process.env.REACT_APP_BASE_URL
      const baseUrl = API_BASE_URL?.replace("/api", "") || "";  

    const path = file?.filepath || file?.path;
    if (!path) return;

    const finalUrl = path.startsWith("http")
      ? path
      : `${baseUrl}${path.startsWith("/") ? path : "/" + path}`;

    setPreviewFile(null);
    setPreviewFileUrl(finalUrl);
    setPreviewOpen(true);
  };

  useEffect(() => {
    breadcrumbContext?.setBreadcrumbItems?.([
      { text: "Drug Transfer", link: TRANSFER_PATHS.LIST },
      { text: "View Transfer Record" },
    ]);

    return () => breadcrumbContext?.setBreadcrumbItems?.([]);
  }, []);

  const { SelectIdQuery } = useTransferService();
  const selectQuery = SelectIdQuery(Number(id));

  const data = selectQuery.data?.data?.[0];

  useEffect(() => {
    if (data?.transfertypetext.toLowerCase().includes("study ➝ study")) {
      setfromLabel("From Study")
      settoLabel("To Study")
      setStudycodelabel(false)
    }
    else {
      setfromLabel("From Location")
      settoLabel("To Location")
      setStudycodelabel(true)
    }
  }, [data])

  const remaining = Number(data?.totalquantity || 0) - Number(data?.transferquantity || 0);

  /* ── Loading ── */
  if (selectQuery.isLoading || selectQuery.isFetching || !data) {
    return (
      <Container fluid className={`px-0 ${formStyles.pageContainer}`}>
        <FullPageLoader />
      </Container>
    );
  }

  /* ── Not Found ── */
  if (!data) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <i className="ri-error-warning-line display-3 text-warning mb-3 d-block" />
            <h4 className="fw-600">Record Not Found</h4>
            <p className="text-muted">
              The Transfer record you are looking for does not exist or has been removed.
            </p>

          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <div className="d-flex align-items-center gap-3">
              <h3 className="fw-700 mb-1">Transfer Details: {data.transfernumber}</h3>
            </div>
            <p className="text-muted mb-0">
              Transfer Type: {data.transfertypetext}&nbsp;|&nbsp;
              Product: {data.productname}&nbsp;|&nbsp;
              Batch: {data.batchlotno}
            </p>
          </div>

        </div>

        <div className="d-flex flex-column gap-0.5">

          {/* 1 — Transfer Details */}
          <SectionCard
            icon="ri-exchange-line"
            title="Transfer Details"
            subtitle="Basic information about the transfer origin and destination."
          >
            <Row>
              <DetailField label="Transfer Type" value={data.transfertypetext} columns={3} />
              <DetailField label={fromlabel} value={data.transferfrom} columns={3} />
              <DetailField label={tolabel} value={data.transferto} columns={3} />
              {studycodelabel && (
                <DetailField label="Studycode" value={data.studycode} columns={3} />
              )}
              <DetailField label="Ip category" value={data.ipcategory} columns={3} />
              <DetailField label="Product Name" value={data.productname} columns={3} />
              <DetailField label="Batch / Lot No" value={data.batchlotno} columns={3} />
              <DetailField label="Dosage Form" value={data.dosageform} columns={3} />
            </Row>
          </SectionCard>

          {/* 2 — Quantity Movement */}
          <SectionCard
            icon="ri-scales-3-line"
            title="Quantity Movement"
            subtitle="Stock quantity updates."
          >
            <Row>
              <DetailField label="Quantity Type" value={data.quantitytype} columns={3} />
              <DetailField label="Total Available" value={data.totalquantity} columns={3} />
              <DetailField label="Quantity Transferred" value={data.transferquantity} columns={3} />
              <DetailField label="Quantity Remaining" value={remaining} columns={3} />
            </Row>
          </SectionCard>

          {/* 3 — Documentation Attached */}
          <SectionCard
            icon="ri-attachment-line"
            title="Documentation Attached & Remarks"
            subtitle="List of documents included in the package and additional notes."
          >
            {/* Attachments */}
            {data.attachments && data.attachments.length > 0 ? (
              <div className="d-flex flex-column gap-0.5">
                <p className="text-muted fs-12 text-uppercase fw-medium ls-1 mb-2 d-block">Uploaded Documents</p>

                <Row className="gap-2 px-0 w-100 mx-auto">
                  {data.attachments.map((file: any, idx: number) => (
                    <Col
                      key={idx}
                      md={3}
                      className={`px-0 ${addVolunteerStyle.ipInventory_wrapper}`}
                    >
                      <div className={`${addVolunteerStyle.attachmentCard} h-100`}>

                        {/* FILE INFO */}
                        <div className={addVolunteerStyle.fileInfo}>
                          <div className={addVolunteerStyle.fileIcon}>
                            <i className="ri-file-pdf-line text-danger" />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className={addVolunteerStyle.fileName}>
                              <TruncatedText text={file.filename} />
                            </div>
                            <div className={addVolunteerStyle.fileMeta}>
                              <span className="text-uppercase font-800 text-primary me-2" style={{ fontSize: '10px' }}>
                                {file.attachmenttypetext}
                              </span>
                              <br />
                              <span className="text-uppercase">{file.filetype || "Document"}</span>
                            </div>
                          </div>
                        </div>

                        {/* ACTIONS */}
                        <div className={addVolunteerStyle.fileActions}>
                          <i
                            className="ri-eye-line text-primary"
                            title="Preview"
                            style={{ cursor: "pointer" }}
                            onClick={() => handlePreview(file)}
                          />
                        </div>

                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            ) : (
              <div>
                <p className="text-muted fs-12 text-uppercase fw-medium ls-1 mb-2 d-block">Uploaded Documents</p>
                <span className="text-muted fst-italic">No attachments uploaded.</span>
              </div>
            )}

            {/* Comments */}
            <Row>
              <Col md={12}>
                <label className="text-muted fs-12 mb-2 text-uppercase fw-600 ls-1 d-block mt-4">
                  Remarks / Additional Notes
                </label>
                <div
                  className="p-3 bg-light rounded-3 border"
                  style={{ whiteSpace: "pre-wrap", lineHeight: "1.6" }}
                >
                  {data.remarks || "No remarks provided."}
                </div>
              </Col>
            </Row>
          </SectionCard>
        </div>
        <FilePreviewModal
          file={previewFile}
          fileUrl={previewFileUrl}
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      </Container>
    </div>
  );
};

export default ViewTransfer;
